const APP_BUILD_ID: string | null = process.env.REACT_APP_BUILD_ID || null;

// localStorage key gating repeat prompts to once per build id, written only when the reload fires.
export const DISMISSED_VERSION_KEY = 'pos.dismissedVersion';

const VERSION_MANIFEST_PATH = '/version.json';
// Coalesce a focus+visibility+pageshow(+wake) burst into a single manifest fetch.
const CHECK_COOLDOWN_MS = 2000;
// Without a deadline a hung request would pin inFlightCheck and kill detection for the tab.
const MANIFEST_TIMEOUT_MS = 5000;

let inFlightCheck: Promise<string | null> | null = null;
let lastCheckAt = 0;
// Same-session dedupe so a still-pending prompt never re-toasts before its reload fires.
const promptedThisSession = new Set<string>();

// Fetches the deployed build id from the cache-busted manifest; any failure → null (fail safe).
async function fetchDeployedVersion(): Promise<string | null> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), MANIFEST_TIMEOUT_MS);
  try {
    const res = await fetch(`${VERSION_MANIFEST_PATH}?t=${Date.now()}`, {
      cache: 'no-store',
      signal: controller.signal,
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { version?: unknown };
    return typeof data.version === 'string' && data.version ? data.version : null;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

// True only when both ids are present AND differ; a missing id is never treated as stale.
function isVersionMismatch(deployed: string | null): boolean {
  return APP_BUILD_ID != null && deployed != null && deployed !== APP_BUILD_ID;
}

function wasVersionPrompted(version: string): boolean {
  if (promptedThisSession.has(version)) return true;
  try {
    return localStorage.getItem(DISMISSED_VERSION_KEY) === version;
  } catch {
    return false;
  }
}

// Persisted at APPLY time only: a device that dies mid-defer must still detect the build next boot.
export function markVersionPrompted(version: string): void {
  try {
    localStorage.setItem(DISMISSED_VERSION_KEY, version);
  } catch {
    // localStorage may be unavailable (private mode); the in-memory set still bounds repeat toasts.
  }
}

// Coalesced staleness check: returns a newly-deployed, not-yet-prompted build id, else null.
export async function checkForStaleVersion(): Promise<string | null> {
  if (!APP_BUILD_ID) return null;
  if (inFlightCheck) return inFlightCheck;
  const now = Date.now();
  if (now - lastCheckAt < CHECK_COOLDOWN_MS) return null;
  lastCheckAt = now;
  inFlightCheck = (async () => {
    const deployed = await fetchDeployedVersion();
    if (deployed == null || !isVersionMismatch(deployed) || wasVersionPrompted(deployed)) {
      return null;
    }
    promptedThisSession.add(deployed);
    return deployed;
  })();
  try {
    return await inFlightCheck;
  } finally {
    inFlightCheck = null;
  }
}
